import { Button } from "@/components/ui/button";
import { FileDown, CheckCircle2, AlertTriangle, Scale } from "lucide-react";
import { lookupDoseLabel } from "@/lib/pharmacyFirst/pathways/aht/ahtDoseLabels";
import { getDrug, pgdRegistry } from "@/lib/pharmacyFirst";

/** One row in a section driven by the audit rather than by fixed form fields. */
export interface SummaryCheckRow {
  label: string;
  value: string;
  /**
   * "risk" — exclusion question, so Yes is red and No green.
   * "benefit" — inclusion question, so Yes is green and No red.
   * Omitted renders neutral.
   */
  tone?: "risk" | "benefit";
}

export interface PGDWeightManagementSummaryData {
  patientConsent: string;
  treatmentForSelf: string;
  consultationDate: string;
  consultationTime: string;
  consultationMethod: string;
  height: string;
  weight: string;
  bmi: string;
  waistCircumference: string;
  ageConfirmed: string;
  patientAge: string;
  bmiEligibility: string;
  comorbidities: string[];
  comorbidityOther: string;
  willingDietExercise: string;
  pregnant: string;
  breastfeeding: string;
  otherWeightLossMeds: string;
  historyPancreatitis: string;
  cholelithiasis: string;
  heartFailureReduced: string;
  endocrinologicalObesity: string;
  eatingDisorder: string;
  weightGainMedication: string;
  severeRenalImpairment: string;
  hepaticImpairment: string;
  insulinSulfonylureas: string;
  poorlyControlledDiabetes: string;
  ibdGastroparesis: string;
  hypersensitivity: string;
  tachycardia: string;
  elderlyPatient: string;
  mildModerateRenal: string;
  thyroidDisease: string;
  warfarinUse: string;
  oralContraceptiveUse: string;
  hrtUse: string;
  upcomingSurgery: string;
  cautionNotes: string;
  causesOfWeightGain: string;
  lifestyleDietExercise: string;
  previousWeightLossAttempts: string;
  mentalHealthFactors: string;
  otherDiseaseStates: string;
  patientExpectations: string;
  currentMedications: string;
  currentDoseWeek: string;
  currentDose: string;
  // Pathway-selected dose: when present, the Dose row is derived from this
  // via the AHT dose label lookup rather than from the form's currentDose.
  selectedDoseStep?: string;
  drugRef?: string;
  quantitySupplied: string;
  pensSupplied: string;
  needlesSupplied: string;
  sharpsBinSupplied: string;
  batchNumber: string;
  expiryDate: string;
  productBrand: string;
  injectionSite: string;
  firstInjectionSupervised: string;
  giAdviceGiven: string;
  dehydrationAdviceGiven: string;
  contraceptionAdviceGiven: string;
  penUsageDemonstrated: string;
  dietExerciseAdviceGiven: string;
  nhsWeightLossPlanDiscussed: string;
  pilProvided: string;
  safetyNetting: string;
  followUpDate: string;
  followUpNotes: string;
  outcome: string;
  exclusionReason: string;
  declineReason: string;
  referralReason: string;
  referralDestination: string;
  internalNotes: string;
  /** Treatment selection narrative: why this drug, what the patient preferred. */
  selectionRows?: SummaryCheckRow[];
  /** Screening findings the fixed rows have nowhere to put — the per-drug
   *  exclusions outside liraglutide's field set. */
  additionalChecks?: SummaryCheckRow[];
}

interface Prescription {
  id: string;
  prescriptionItems: any[];
  prescriptionCategory?: string;
  status?: string;
  issuedAt?: string;
  printedAt?: string;
  pharmacy?: string;
}

interface PGDWeightManagementSummaryProps {
  isDarkMode?: boolean;
  data: PGDWeightManagementSummaryData;
  prescriptions: Prescription[];
  /** Shows the completion button. Omit when the consultation is read-only. */
  onComplete?: () => void;
  isCompleted?: boolean;
}

export function PGDWeightManagementSummary({
  isDarkMode = false,
  data,
  prescriptions,
  onComplete,
  isCompleted = false,
}: PGDWeightManagementSummaryProps) {
  // Everything product-specific on this screen is resolved from data.drugRef so
  // one summary serves all six AHT weight drugs. With no drugRef the headings
  // stay generic rather than naming a drug that was never supplied.
  const drugRef = data.drugRef;
  const drug = (() => {
    if (!drugRef) return null;
    try {
      return getDrug(drugRef);
    } catch {
      return null;
    }
  })();
  const pgd = drugRef
    ? (Object.values(pgdRegistry).find((p) => p.drugRef === drugRef) ?? null)
    : null;

  const drugName = drug?.name ?? pgd?.indication ?? null;
  // With no resolvable drug, assume oral — showing pen and injection-site rows
  // for a drug we cannot identify is worse than omitting them.
  const isInjectable = drug?.route === "subcutaneous";
  const pgdTitle = drugName
    ? `PGD: ${drugName} — Weight Management`
    : "PGD: Weight Management";
  const pgdStamp = pgd
    ? `${pgd.commissioner} ${pgd.reference} v${pgd.version}`
    : null;
  const ageRange = pgd?.population?.ageRange;
  const ageCheckLabel = ageRange
    ? `Age ${ageRange.min}-${ageRange.max}`
    : "Age within PGD range";
  const fileSlug = (drugRef ?? "WeightManagement").replace(/[^a-zA-Z0-9]+/g, "_");

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "Not specified";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatTime = (timeStr: string) => {
    if (!timeStr) return "Not specified";
    return timeStr;
  };

  const getOutcomeLabel = (outcome: string) => {
    const labels: Record<string, string> = {
      "supply-made": "Supply Made Under PGD",
      "excluded": "Patient Excluded - Criteria Not Met",
      "patient-declined": "Patient Declined Treatment",
      "refer-clinician": "Referred to Clinician / Specialist",
    };
    return labels[outcome] || outcome;
  };

  const getBMICategoryLabel = (bmiEligibility: string) => {
    const labels: Record<string, string> = {
      "bmi-30-plus": "BMI ≥30 kg/m² (Obese)",
      "bmi-27-30-comorbidity": "BMI 27-30 kg/m² with comorbidities",
      "not-eligible": "Not eligible",
    };
    return labels[bmiEligibility] || bmiEligibility;
  };

  // Liraglutide's weekly escalation. Only valid for liraglutide — every other
  // AHT weight drug has its own ladder, so fall back to the bare week label.
  const getDoseWeekLabel = (week: string) => {
    if (!week) return week;
    const liraglutideLabels: Record<string, string> = {
      "week-1": "Week 1 (0.6mg)",
      "week-2": "Week 2 (1.2mg)",
      "week-3": "Week 3 (1.8mg)",
      "week-4": "Week 4 (2.4mg)",
      "maintenance": "Maintenance (3.0mg)",
    };
    if (drugRef === "liraglutide") return liraglutideLabels[week] || week;
    if (week === "maintenance") return "Maintenance";
    const match = /^week-(\d+)$/.exec(week);
    return match ? `Week ${match[1]}` : week;
  };

  const getInjectionSiteLabel = (site: string) => {
    const labels: Record<string, string> = {
      "abdomen": "Lower abdomen",
      "thigh": "Thigh",
      "upper-arm": "Upper arm (caregiver)",
    };
    return labels[site] || site;
  };

  const getComorbidityLabel = (id: string) => {
    const labels: Record<string, string> = {
      "dysglycaemia": "Dysglycaemia (pre-diabetes / T2DM)",
      "hypertension": "Hypertension",
      "dyslipidaemia": "Dyslipidaemia",
      "sleep-apnoea": "Obstructive sleep apnoea",
      "other": "Other",
    };
    return labels[id] || id;
  };

  const yesNoLabel = (val: string) => {
    if (val === "yes") return "Yes";
    if (val === "no") return "No";
    if (val === "not-applicable") return "N/A";
    return "Not specified";
  };

  const doseLabel =
    data.selectedDoseStep && data.drugRef
      ? (lookupDoseLabel(data.drugRef, data.selectedDoseStep) ?? data.currentDose)
      : data.currentDose || getDoseWeekLabel(data.currentDoseWeek);

  const downloadGPNotification = () => {
    const notificationContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>GP Notification - ${pgdTitle}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 1.25rem; max-width: 800px; margin: 0 auto; }
          h1 { color: #7c3aed; font-size: 1.25rem; }
          h2 { color: #374151; font-size: 1rem; border-bottom: 1px solid #e5e7eb; padding-bottom: 0.5rem; }
          .section { margin-bottom: 1rem; }
          .label { font-weight: bold; display: inline; }
          .value { display: inline; margin-left: 0.25rem; }
          .row { margin-bottom: 0.25rem; }
          .alert { background: #fef3c7; border: 1px solid #f59e0b; padding: 0.75rem; border-radius: 0.5rem; margin: 0.5rem 0; }
        </style>
      </head>
      <body>
        <h1>${pgdTitle}</h1>
        <p><em>${pgdStamp ? `${pgdStamp} | ` : ""}Supplied via Patient Group Direction</em></p>
        
        <h2>Consultation Details</h2>
        <div class="section">
          <div class="row"><span class="label">Date:</span> <span class="value">${formatDate(data.consultationDate)} at ${formatTime(data.consultationTime)}</span></div>
          <div class="row"><span class="label">Method:</span> <span class="value">${data.consultationMethod}</span></div>
          <div class="row"><span class="label">Consent:</span> <span class="value">${yesNoLabel(data.patientConsent)}</span></div>
        </div>

        <h2>Patient Measurements</h2>
        <div class="section">
          <div class="row"><span class="label">Height:</span> <span class="value">${data.height} cm</span></div>
          <div class="row"><span class="label">Weight:</span> <span class="value">${data.weight} kg</span></div>
          <div class="row"><span class="label">BMI:</span> <span class="value">${data.bmi} kg/m²</span></div>
          ${data.waistCircumference ? `<div class="row"><span class="label">Waist:</span> <span class="value">${data.waistCircumference} cm</span></div>` : ""}
        </div>

        <h2>Treatment</h2>
        <div class="section">
          <div class="row"><span class="label">Dose:</span> <span class="value">${doseLabel}</span></div>
          <div class="row"><span class="label">Product:</span> <span class="value">${data.productBrand || "Not specified"}</span></div>
          <div class="row"><span class="label">Quantity:</span> <span class="value">${data.quantitySupplied || "Not specified"}</span></div>
          <div class="row"><span class="label">Batch:</span> <span class="value">${data.batchNumber || "Not specified"}</span></div>
        </div>

        <h2>Outcome</h2>
        <div class="section">
          <div class="row"><span class="label">Outcome:</span> <span class="value">${getOutcomeLabel(data.outcome)}</span></div>
          ${data.followUpDate ? `<div class="row"><span class="label">Follow-up:</span> <span class="value">${formatDate(data.followUpDate)}</span></div>` : ""}
        </div>
      </body>
      </html>
    `;

    const blob = new Blob([notificationContent], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `GP_Notification_WeightManagement_${fileSlug}_${new Date().toISOString().split("T")[0]}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const SummaryRow = ({
    label,
    value,
    highlight,
    tone,
  }: {
    label: string;
    value: string;
    highlight?: boolean;
    tone?: "risk" | "benefit";
  }) => {
    // Inclusion criteria read the opposite way round to exclusion criteria —
    // "Yes" is the safe answer — so the safe answer is the green one either way.
    const safeAnswer = tone === "benefit" ? "Yes" : "No";
    const coloured = highlight || tone !== undefined;
    return (
      <div className={`flex justify-between items-start py-1.5 ${isDarkMode ? "border-gray-700" : "border-gray-100"} border-b last:border-0`}>
        <span className={`text-xs font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>{label}</span>
        <span className={`text-xs text-right max-w-[60%] ${
          coloured && (value === "Yes" || value === "No")
            ? value === safeAnswer ? "text-green-600 font-semibold" : "text-red-500 font-semibold"
            : isDarkMode ? "text-gray-200" : "text-gray-800"
        }`}>{value || "Not specified"}</span>
      </div>
    );
  };

  const SummarySection = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <div className={`rounded-xl border p-4 ${isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-white"}`}>
      <h3 className={`text-sm font-semibold mb-3 ${isDarkMode ? "text-white" : "text-gray-900"}`}>{title}</h3>
      {children}
    </div>
  );

  return (
    <div className="space-y-4">
      {/* PGD Header */}
      <div className={`p-3 rounded-lg border ${isDarkMode ? "border-purple-700 bg-purple-900/30" : "border-purple-200 bg-purple-50"}`}>
        <div className="flex items-center gap-2">
          <Scale className={`h-4 w-4 ${isDarkMode ? "text-purple-300" : "text-purple-600"}`} />
          <span className={`text-sm font-semibold ${isDarkMode ? "text-purple-300" : "text-purple-700"}`}>
            {pgdTitle}
          </span>
        </div>
        <p className={`text-xs mt-1 ${isDarkMode ? "text-purple-400" : "text-purple-600"}`}>
          {pgdStamp ? `${pgdStamp} | ` : ""}Consultation Summary
        </p>
      </div>

      {/* Outcome banner */}
      <div className={`p-3 rounded-lg flex items-center gap-2 ${
        data.outcome === "supply-made"
          ? "bg-green-50 border border-green-200"
          : data.outcome === "excluded"
          ? "bg-red-50 border border-red-200"
          : data.outcome === "patient-declined"
          ? "bg-amber-50 border border-amber-200"
          : "bg-blue-50 border border-blue-200"
      }`}>
        {data.outcome === "supply-made" ? (
          <CheckCircle2 className="h-5 w-5 text-green-600" />
        ) : (
          <AlertTriangle className="h-5 w-5 text-amber-600" />
        )}
        <div>
          <p className={`text-sm font-semibold ${
            data.outcome === "supply-made" ? "text-green-700" :
            data.outcome === "excluded" ? "text-red-700" :
            "text-amber-700"
          }`}>
            {getOutcomeLabel(data.outcome)}
          </p>
        </div>
      </div>

      {/* Consultation Details */}
      <SummarySection title="Consultation Details">
        <SummaryRow label="Date" value={formatDate(data.consultationDate)} />
        <SummaryRow label="Time" value={formatTime(data.consultationTime)} />
        <SummaryRow label="Method" value={data.consultationMethod === "face-to-face" ? "Face to Face" : data.consultationMethod === "video" ? "Video Link" : data.consultationMethod} />
        <SummaryRow label="Patient Consent" value={yesNoLabel(data.patientConsent)} />
        <SummaryRow label="Treatment for Self" value={yesNoLabel(data.treatmentForSelf)} />
      </SummarySection>

      {/* Patient Measurements */}
      <SummarySection title="Patient Measurements">
        <SummaryRow label="Height" value={data.height ? `${data.height} cm` : ""} />
        <SummaryRow label="Weight" value={data.weight ? `${data.weight} kg` : ""} />
        <SummaryRow label="BMI" value={data.bmi ? `${data.bmi} kg/m²` : ""} />
        <SummaryRow label="BMI Category" value={getBMICategoryLabel(data.bmiEligibility)} />
        {data.waistCircumference && <SummaryRow label="Waist Circumference" value={`${data.waistCircumference} cm`} />}
        {data.comorbidities && data.comorbidities.length > 0 && (
          <SummaryRow label="Comorbidities" value={data.comorbidities.map(c => getComorbidityLabel(c)).join(", ") + (data.comorbidityOther ? ` (${data.comorbidityOther})` : "")} />
        )}
      </SummarySection>

      {/* Treatment selection — why this drug, and what the patient preferred */}
      {data.selectionRows && data.selectionRows.length > 0 && (
        <SummarySection title="Treatment Selection">
          {data.selectionRows.map((row) => (
            <SummaryRow key={row.label} label={row.label} value={row.value} tone={row.tone} />
          ))}
        </SummarySection>
      )}

      {/* Eligibility */}
      <SummarySection title="Eligibility Screening">
        {/* The first two are inclusion criteria, so Yes is the safe answer and
            reads green; the rest are exclusions and read the other way round. */}
        <SummaryRow label={ageCheckLabel} value={yesNoLabel(data.ageConfirmed)} tone="benefit" />
        <SummaryRow label="Willing to diet & exercise" value={yesNoLabel(data.willingDietExercise)} tone="benefit" />
        <SummaryRow label="Pregnant" value={yesNoLabel(data.pregnant)} highlight />
        <SummaryRow label="Breastfeeding" value={yesNoLabel(data.breastfeeding)} highlight />
        <SummaryRow label="Other weight loss medications" value={yesNoLabel(data.otherWeightLossMeds)} highlight />
        <SummaryRow label="History of pancreatitis" value={yesNoLabel(data.historyPancreatitis)} highlight />
        <SummaryRow label="Gallstones / cholecystitis" value={yesNoLabel(data.cholelithiasis)} highlight />
        <SummaryRow label="Heart failure (HFrEF)" value={yesNoLabel(data.heartFailureReduced)} highlight />
        <SummaryRow label="Endocrinological obesity" value={yesNoLabel(data.endocrinologicalObesity)} highlight />
        <SummaryRow label="Eating disorder history" value={yesNoLabel(data.eatingDisorder)} highlight />
        <SummaryRow label="Weight gain medication" value={yesNoLabel(data.weightGainMedication)} highlight />
        <SummaryRow label="Severe renal impairment" value={yesNoLabel(data.severeRenalImpairment)} highlight />
        <SummaryRow label="Hepatic impairment" value={yesNoLabel(data.hepaticImpairment)} highlight />
        <SummaryRow label="Insulin / sulfonylureas" value={yesNoLabel(data.insulinSulfonylureas)} highlight />
        <SummaryRow label="Poorly controlled T2DM" value={yesNoLabel(data.poorlyControlledDiabetes)} highlight />
        <SummaryRow label="IBD / gastroparesis" value={yesNoLabel(data.ibdGastroparesis)} highlight />
        <SummaryRow label="Hypersensitivity" value={yesNoLabel(data.hypersensitivity)} highlight />
      </SummarySection>

      {/* Per-drug exclusions outside liraglutide's fixed field set — Mysimba's
          CNS criteria, orlistat's malabsorption criteria and the like. */}
      {data.additionalChecks && data.additionalChecks.length > 0 && (
        <SummarySection title="Additional Screening Findings">
          {data.additionalChecks.map((row) => (
            <SummaryRow key={row.label} label={row.label} value={row.value} tone={row.tone} />
          ))}
        </SummarySection>
      )}

      {/* Cautions */}
      {(data.tachycardia === "yes" || data.elderlyPatient === "yes" || data.mildModerateRenal === "yes" ||
        data.thyroidDisease === "yes" || data.warfarinUse === "yes" || data.oralContraceptiveUse === "yes" ||
        data.hrtUse === "yes" || data.upcomingSurgery === "yes" || data.cautionNotes) && (
        <SummarySection title="Cautions Identified">
          {data.tachycardia === "yes" && <SummaryRow label="Tachycardia" value="Yes - monitor heart rate" />}
          {data.elderlyPatient === "yes" && <SummaryRow label="Elderly (65-75)" value="Yes - use with caution" />}
          {data.mildModerateRenal === "yes" && <SummaryRow label="Mild/moderate renal" value="Yes - monitor GI effects" />}
          {data.thyroidDisease === "yes" && <SummaryRow label="Thyroid disease" value="Yes - specialist advice" />}
          {data.warfarinUse === "yes" && <SummaryRow label="Warfarin use" value="Yes - frequent INR monitoring" />}
          {data.oralContraceptiveUse === "yes" && <SummaryRow label="Oral contraceptive" value="Yes - consider alternative method" />}
          {data.hrtUse === "yes" && <SummaryRow label="HRT use" value="Yes - consider non-oral products" />}
          {data.upcomingSurgery === "yes" && <SummaryRow label="Upcoming surgery" value="Yes - inform surgical team" />}
          {data.cautionNotes && <SummaryRow label="Notes" value={data.cautionNotes} />}
        </SummarySection>
      )}

      {/* Assessment */}
      {(data.causesOfWeightGain || data.lifestyleDietExercise || data.previousWeightLossAttempts ||
        data.patientExpectations || data.currentMedications) && (
        <SummarySection title="Initial Assessment">
          {data.causesOfWeightGain && <SummaryRow label="Causes of weight gain" value={data.causesOfWeightGain} />}
          {data.lifestyleDietExercise && <SummaryRow label="Lifestyle / diet / exercise" value={data.lifestyleDietExercise} />}
          {data.previousWeightLossAttempts && <SummaryRow label="Previous attempts" value={data.previousWeightLossAttempts} />}
          {data.mentalHealthFactors && <SummaryRow label="Mental health factors" value={data.mentalHealthFactors} />}
          {data.otherDiseaseStates && <SummaryRow label="Other conditions" value={data.otherDiseaseStates} />}
          {data.patientExpectations && <SummaryRow label="Patient expectations" value={data.patientExpectations} />}
          {data.currentMedications && <SummaryRow label="Current medications" value={data.currentMedications} />}
        </SummarySection>
      )}

      {/* Dosing & Supply */}
      {data.outcome === "supply-made" && (
        <SummarySection title="Dosing & Supply">
          {!data.selectedDoseStep && data.currentDoseWeek && (
            <SummaryRow label="Dose week" value={getDoseWeekLabel(data.currentDoseWeek)} />
          )}
          {/* Dose, product and quantity always come off the PGD supply record.
              The rest have no capture point in the pathway yet, so they are only
              shown once something actually fills them. */}
          <SummaryRow label="Dose" value={doseLabel} />
          <SummaryRow label="Product" value={data.productBrand} />
          <SummaryRow label="Total quantity" value={data.quantitySupplied} />
          {isInjectable && data.pensSupplied && (
            <SummaryRow label="Pens supplied" value={data.pensSupplied} />
          )}
          {isInjectable && data.needlesSupplied && (
            <SummaryRow label="Needles supplied" value={data.needlesSupplied} />
          )}
          {isInjectable && data.sharpsBinSupplied && (
            <SummaryRow label="Sharps bin" value={yesNoLabel(data.sharpsBinSupplied)} />
          )}
          {data.batchNumber && <SummaryRow label="Batch number" value={data.batchNumber} />}
          {data.expiryDate && (
            <SummaryRow label="Expiry date" value={formatDate(data.expiryDate)} />
          )}
          {isInjectable && data.injectionSite && (
            <SummaryRow label="Injection site" value={getInjectionSiteLabel(data.injectionSite)} />
          )}
          {isInjectable && data.firstInjectionSupervised && (
            <SummaryRow label="First injection supervised" value={yesNoLabel(data.firstInjectionSupervised)} />
          )}
        </SummarySection>
      )}

      {/* Advice Given */}
      <SummarySection title="Advice & Follow-Up">
        <SummaryRow label="GI side effects advice" value={yesNoLabel(data.giAdviceGiven)} />
        <SummaryRow label="Dehydration advice" value={yesNoLabel(data.dehydrationAdviceGiven)} />
        <SummaryRow label="Contraception advice" value={yesNoLabel(data.contraceptionAdviceGiven)} />
        {isInjectable && (
          <SummaryRow label="Pen usage demonstrated" value={yesNoLabel(data.penUsageDemonstrated)} />
        )}
        <SummaryRow label="Diet & exercise advice" value={yesNoLabel(data.dietExerciseAdviceGiven)} />
        <SummaryRow label="NHS Weight Loss Plan" value={yesNoLabel(data.nhsWeightLossPlanDiscussed)} />
        <SummaryRow label="PIL provided" value={yesNoLabel(data.pilProvided)} />
        {data.safetyNetting && <SummaryRow label="Safety netting" value={data.safetyNetting} />}
        {data.followUpDate && <SummaryRow label="Follow-up date" value={formatDate(data.followUpDate)} />}
        {data.followUpNotes && <SummaryRow label="Follow-up notes" value={data.followUpNotes} />}
      </SummarySection>

      {/* Exclusion / Decline / Referral details */}
      {data.outcome === "excluded" && data.exclusionReason && (
        <SummarySection title="Exclusion Details">
          <SummaryRow label="Reason" value={data.exclusionReason} />
          {data.referralDestination && <SummaryRow label="Referral destination" value={data.referralDestination} />}
        </SummarySection>
      )}
      {data.outcome === "patient-declined" && data.declineReason && (
        <SummarySection title="Decline Details">
          <SummaryRow label="Reason" value={data.declineReason} />
        </SummarySection>
      )}
      {data.outcome === "refer-clinician" && (
        <SummarySection title="Referral Details">
          {data.referralReason && <SummaryRow label="Reason" value={data.referralReason} />}
          {data.referralDestination && <SummaryRow label="Destination" value={data.referralDestination} />}
        </SummarySection>
      )}

      {/* Internal Notes */}
      {data.internalNotes && (
        <SummarySection title="Internal Notes">
          <p className={`text-xs ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>{data.internalNotes}</p>
        </SummarySection>
      )}

      {/* Prescriptions */}
      {prescriptions.length > 0 && (
        <SummarySection title="PGD Supplies">
          {prescriptions.map((rx, index) => (
            <div key={rx.id} className={`p-2 rounded border mb-2 ${isDarkMode ? "border-gray-600 bg-gray-700" : "border-gray-200 bg-gray-50"}`}>
              <p className={`text-xs font-medium ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}>
                PGD Supply {index + 1} — {rx.prescriptionItems?.length || 0} items
              </p>
              {rx.prescriptionItems?.map((item: any, i: number) => (
                <p key={i} className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
                  {item.medication || item.name} {item.dose && `— ${item.dose}`}
                </p>
              ))}
            </div>
          ))}
        </SummarySection>
      )}

      {/* Actions */}
      <div className="flex gap-2 pt-2">
        <Button variant="outline" size="sm" onClick={downloadGPNotification}>
          <FileDown className="h-3 w-3 mr-1" /> Download GP Notification
        </Button>
      </div>

      {/* PGD Footer */}
      <div className={`text-xs p-2 rounded ${isDarkMode ? "text-gray-500 bg-gray-800" : "text-gray-400 bg-gray-50"}`}>
        <p>
          Supplied via {pgdTitle}
          {pgdStamp ? ` (${pgdStamp})` : ""}
        </p>
        <p>Records should be retained for 8 years for audit purposes.</p>
      </div>

      {isCompleted ? (
        <div className={`flex items-center justify-center gap-2 p-3 rounded-lg ${isDarkMode ? "bg-green-900/30 border border-green-700" : "bg-green-50 border border-green-200"}`}>
          <CheckCircle2 className={`h-5 w-5 ${isDarkMode ? "text-green-400" : "text-green-600"}`} />
          <span className={`font-semibold text-sm ${isDarkMode ? "text-green-300" : "text-green-700"}`}>
            Consultation Completed
          </span>
        </div>
      ) : (
        onComplete && (
          <Button onClick={onComplete} size="lg" className="w-full">
            Complete Consultation
          </Button>
        )
      )}
    </div>
  );
}
